import { bossAt, UNSLEEPING, UNSLEEPING_IDS, UNSLEEPING_OF, UNSLEEPING_RELIC, type UnsleepingId } from '@somnhold/content';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';
import { idiv, imin, mulBp } from '../math/fixed.ts';
import type { UnsleepingOutcome } from '../types.ts';
import type { WaveSpec } from '../wave.ts';
import { event } from './core.ts';
import { eraOf } from './eras.ts';
import { grantRelic } from './incursion.ts';
import { levelMarea } from './level.ts';
import type { CampaignState, Ms } from './types.ts';

/*
 * The Unsleeping in a campaign (docs/ANTIHEROES.md): which bastion wave brings one, how strong he comes, and
 * what the group's fight with him leaves (fled: he comes back later for the hero who hurt him most; felled:
 * for good, with a trophy).
 */

/** The party's mean level above 1, in bp of a level. */
const levelAbove = (s: CampaignState): number => {
  const active = s.players.filter((p) => !p.left);
  if (!active.length) return 0;
  let levels = 0;
  for (const p of active) levels += p.hero.level;
  return idiv((levels - active.length) * 10000, active.length);
};

/**
 * Which Unsleeping this bastion wave brings, if any (his own draw, so the wave's other draws don't move). The first
 * a group ever meets comes at once and is always the first of the cast (Corvin); after that one wave in `every`
 * brings one (at most one a Marea), drawn among those not felled — one who fled only from a later Marea than his
 * flight. An admin may
 * call one to a wave (to watch him live), whatever the draw says.
 */
const chosen = (s: CampaignState, marea: number, wave: number): UnsleepingId | undefined => {
  const all = s.unsleeping ?? {};
  const f = s.unsleepingForce;
  if (f && f.marea === marea && f.wave === wave) {
    const id = f.id ?? UNSLEEPING_IDS.find((k) => !all[k]?.fallen && !all[k]?.home);
    return id && !all[id]?.fallen && !all[id]?.home ? id : undefined;
  }
  if (!UNSLEEPING_IDS.some((k) => all[k])) return UNSLEEPING.firstAtOnce ? UNSLEEPING_IDS[0] : undefined;
  if (UNSLEEPING.onePerMarea && UNSLEEPING_IDS.some((k) => all[k]?.marea === marea)) return undefined;
  const free = UNSLEEPING_IDS.filter((k) => {
    const r = all[k];
    return !r || (!r.fallen && !r.home && marea > r.marea);
  });
  if (!free.length) return undefined;
  const rng = seedRng(deriveSeed(s.seed, 91000 + marea * 16 + wave));
  const every = eraOf(s) >= 2 ? UNSLEEPING.everyLater : UNSLEEPING.everyEraI;
  if (rngInt(rng, every) !== 0) return undefined;
  return free[rngInt(rng, free.length)];
};

/** The Unsleeping a bastion wave brings, if any: who, how strong, and the hero he hunts. */
export const unsleepingFor = (s: CampaignState, marea: number, wave: number): Pick<WaveSpec, 'unsleeping'> => {
  // From the step the group's level calls for, whatever the calendar says (campaign/level.ts).
  if (levelMarea(s) < UNSLEEPING.fromMarea || bossAt(marea, wave)) return {};
  const id = chosen(s, marea, wave);
  if (!id) return {};
  const r = s.unsleeping?.[id];
  const returns = r?.returns ?? 0;
  const party = imin(UNSLEEPING.hpByParty.length - 1, s.players.filter((p) => !p.left).length);
  const above = levelAbove(s);
  const back = 10000 + returns * UNSLEEPING.returnBp;
  const base = mulBp(UNSLEEPING.hpByParty[party] as number, UNSLEEPING_OF[id].hpBp);
  const hp = mulBp(mulBp(base, 10000 + idiv(UNSLEEPING.hpPerLevelBp * above, 10000)), back);
  const dmgBp = mulBp(10000 + idiv(UNSLEEPING.dmgPerLevelBp * above, 10000), back);
  return { unsleeping: { kind: id, hp, dmgBp, returns, ...(r?.hunt ? { hunt: r.hunt } : {}) } };
};

/** A hero who fought him: whether live (a ghost only gets his relic when nobody fought live). */
export interface UnsleepingFighter {
  pid: string;
  live: boolean;
}

/**
 * Who gets an Unsleeping's relic: whoever fought him live before the ghosts, then whoever has the fewest of their
 * relics (so they go round the group), then the fewest relics of any kind, then the one who struck the last blow,
 * then the one who hurt him most, then by id. Not simply the last blow: that favoured the damage classes (Sebas).
 * Without the fighters (older callers): the last blow, or the one who hurt him most.
 */
export const unsleepingRelicTo = (s: CampaignState, out: UnsleepingOutcome, fighters?: UnsleepingFighter[]): string | undefined => {
  const pool = s.players.filter((p) => !p.left && fighters?.some((f) => f.pid === p.id));
  if (!pool.length) return out.pid || out.top;
  const live = pool.filter((p) => fighters?.some((f) => f.pid === p.id && f.live));
  const cands = live.length ? live : pool;
  const owned = (p: (typeof cands)[number]): string[] => [...p.hero.relics, ...(p.hero.relicBag ?? [])];
  const theirs = (p: (typeof cands)[number]): number => owned(p).filter((r) => UNSLEEPING_IDS.some((k) => UNSLEEPING_RELIC[k] === r)).length;
  const rank = (p: (typeof cands)[number]): number => (p.id === out.pid ? 0 : p.id === out.top ? 1 : 2);
  cands.sort((a, b) => theirs(a) - theirs(b) || owned(a).length - owned(b).length || rank(a) - rank(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return cands[0]?.id;
};

/** After a bastion wave with an Unsleeping in it: what the campaign keeps of the fight. Returns whether he fell. */
export const afterUnsleeping = (s: CampaignState, now: Ms, marea: number, wave: number, out: UnsleepingOutcome, fighters?: UnsleepingFighter[]): boolean => {
  const all = (s.unsleeping ??= {});
  const r = (all[out.id] ??= { returns: 0, marea, wave });
  r.marea = marea;
  r.wave = wave;
  if (out.top) r.hunt = out.top;
  let relic: { relic: string; to: string; stored?: true } | undefined;
  if (out.outcome === 'slain') {
    const first = !r.fallen;
    r.fallen = { pid: out.pid ?? '', marea, wave, at: now };
    delete r.hunt;
    // His relic, once a campaign: to whoever fought him and has the fewest of theirs (see `unsleepingRelicTo`).
    const to = unsleepingRelicTo(s, out, fighters);
    if (first && to) {
      const got = grantRelic(s, to, UNSLEEPING_RELIC[out.id], now);
      if (got) relic = { relic: UNSLEEPING_RELIC[out.id], to, ...(got === 'stored' ? { stored: true as const } : {}) };
      r.relic = true;
    }
  } else if (out.outcome === 'fled') r.returns = imin(UNSLEEPING.maxReturns, r.returns + 1);
  event(s, now, 'unsleeping', { ...(out.pid ? { pid: out.pid } : {}), data: { id: out.id, outcome: out.outcome, returns: r.returns, ...(relic ?? {}) } });
  return out.outcome === 'slain';
};

/**
 * Felled before the relics came (2026-10-30): his relic goes to the hero who struck the last blow, once, when the
 * campaign loads. Returns whether any was given.
 */
export const owedRelics = (s: CampaignState): boolean => {
  let changed = false;
  for (const id of UNSLEEPING_IDS) {
    const r = s.unsleeping?.[id];
    if (!r?.fallen || r.relic) continue;
    const relic = UNSLEEPING_RELIC[id];
    r.relic = true;
    changed = true;
    if (s.players.some((p) => p.hero.relics.includes(relic) || (p.hero.relicBag ?? []).includes(relic))) continue;
    const to = r.fallen.pid;
    if (to && grantRelic(s, to, relic, r.fallen.at)) event(s, r.fallen.at, 'unsleeping', { pid: to, data: { id, outcome: 'slain', returns: r.returns, relic, to } });
  }
  return changed;
};

/** The Unsleeping the group felled for good (their trophies in the bastion). */
export const fallenUnsleeping = (s: CampaignState): UnsleepingId[] => UNSLEEPING_IDS.filter((k) => !!s.unsleeping?.[k]?.fallen);
